import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, UploadIcon } from 'lucide-react';
import { usePortalQuery, usePortalMutation } from '../../hooks/usePortalQuery';
import { createConversation, getProject, listConversations, listDeliverables, updateProject, type DeliverableGroup } from '../../lib/portalApi';
import { can } from '../../lib/portalPermissions';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';
import { Button } from '../../components/ui/Button';
import { inputClass } from '../../components/ui/Field';
import { ProductTag } from '../../components/portal/ProductTag';
import { StatusBadge } from '../../components/portal/StatusBadge';
import { Avatar } from '../../components/portal/Avatar';
import { TaskBoard } from '../../components/portal/TaskBoard';
import { DeliverableList } from '../../components/portal/DeliverableList';
import { UploadDeliverableDialog } from '../../components/portal/UploadDeliverableDialog';
import { MessageThread } from '../../components/portal/MessageThread';
import { EmptyState, InlineError, QueryState } from '../../components/portal/QueryState';
import { displayName, formatDate, PROJECT_STATUS_LABELS, TASK_STATUS_LABELS } from '../../utils/portalFormat';
import type { OrgRole, ProjectDetail, ProjectStatus, TaskStatus } from '../../types/portal';
type Tab = 'overview' | 'tasks' | 'deliverables' | 'messages';
const MEMBER_ROLE_LABELS = {
  lead: 'Lead',
  contributor: 'Contributor',
  observer: 'Observer'
} as const;
export function PortalProject() {
  const {
    orgId = '',
    projectId = ''
  } = useParams<{
    orgId: string;
    projectId: string;
  }>();
  const [params, setParams] = useSearchParams();
  const {
    roleIn
  } = usePortalAuth();
  const role = roleIn(orgId) as OrgRole;
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => getProject(orgId, projectId), [orgId, projectId]);
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'tasks',
    label: 'Tasks'
  }, {
    id: 'deliverables',
    label: 'Deliverables'
  }, ...(can(role, 'messages.view') ? [{
    id: 'messages' as Tab,
    label: 'Messages'
  }] : [])];
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = tabs.some(t => t.id === requested) ? requested as Tab : 'overview';
  return <div data-id="element-3119" data-mp="z5lrawam1dex">
      <Link to={`/portal/orgs/${orgId}/projects`} className="inline-flex items-center gap-1 text-[14px] text-muted hover:text-ink" data-id="element-3120" data-mp="z1xzc1ktcz2b">
        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-3121" data-mp="1rsy62kaj4gxo" />
        Projects in {orgName(orgId)}
      </Link>

      <div className="mt-5" data-id="element-3122" data-mp="1mstzpn3c52l6">
        <QueryState loading={loading} error={error} rows={2} data-id="element-3123" data-mp="1ms0n45hp3iv4">
          {data && <>
              <header className="border-b border-line pb-6" data-id="element-3124" data-mp="1irgpz1x4gf8z">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-3125" data-mp="beosml7tsclm">
                  <ProductTag productId={data.project.productId} data-id="element-3126" data-mp="7d37ydp1uu0v" />
                  <StatusBadge kind="project" status={data.project.status} data-id="element-3127" data-mp="7bju0tqv8v29" />
                </div>
                <h1 className="mt-3 font-display text-[28px] font-light leading-tight text-ink sm:text-[34px]" data-id="element-3128" data-mp="2j21bx4wp3ixa">{data.project.name}</h1>
                <p className="mt-2 text-[14px] text-muted" data-id="element-3129" data-mp="2s14xcfjc4aey">
                  {formatDate(data.project.startDate)} to {formatDate(data.project.endDate)}
                </p>
              </header>

              <div role="tablist" aria-label="Project sections" className="-mx-1 mt-2 flex gap-1 overflow-x-auto border-b border-line" data-id="element-3130" data-mp="31qy85ebnkli5">
                {tabs.map(t => <button key={t.id} role="tab" id={`tab-${t.id}`} aria-selected={tab === t.id} aria-controls={`panel-${t.id}`} onClick={() => setParams(t.id === 'overview' ? {} : {
              tab: t.id
            }, {
              replace: true
            })} className={`relative whitespace-nowrap px-3 py-3.5 text-[15px] transition-colors duration-150 ${tab === t.id ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-3131" data-mp="36rvmtgxgexew">
                    {t.label}
                    {tab === t.id && <span className="absolute inset-x-3 -bottom-px h-0.5 bg-gold" aria-hidden="true" data-id="element-3132" data-mp="l2n63yaz1mxi" />}
                  </button>)}
              </div>

              <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="pt-7" data-id="element-3133" data-mp="v12ys5pnd494">
                {tab === 'overview' && <Overview orgId={orgId} role={role} detail={data} data-id="element-3134" data-mp="7c9zbz9tge9r" />}
                {tab === 'tasks' && <TaskBoard orgId={orgId} projectId={projectId} role={role} people={data.members.map(m => m.user)} data-id="element-3135" data-mp="2e03dmg9p9n3" />}
                {tab === 'deliverables' && <Deliverables orgId={orgId} projectId={projectId} role={role} data-id="element-3136" data-mp="h3ebysc0rn1f" />}
                {tab === 'messages' && <ProjectMessages orgId={orgId} projectId={projectId} projectName={data.project.name} role={role} data-id="element-3137" data-mp="3kqogufr84dgb" />}
              </div>
            </>}
        </QueryState>
      </div>
    </div>;
}
function Overview({
  orgId,
  role,
  detail
}: {
  orgId: string;
  role: OrgRole;
  detail: ProjectDetail;
}) {
  const {
    project,
    members,
    taskSummary
  } = detail;
  const total = Object.values(taskSummary).reduce((a, b) => a + b, 0);
  const order: TaskStatus[] = ['done', 'review', 'in_progress', 'todo'];
  const colour: Record<TaskStatus, string> = {
    done: 'bg-[#6FD39A]',
    review: 'bg-[#F5C26B]',
    in_progress: 'bg-[#8DB8FF]',
    todo: 'bg-line'
  };
  return <div className="grid gap-10 lg:grid-cols-[1fr_320px]" data-id="element-3138" data-mp="2e1bwyklakzng">
      <div className="space-y-10" data-id="element-3139" data-mp="2iznab5aspsaf">
        <section data-id="element-3140" data-mp="2ixsqjub9g67x">
          <h2 className="font-display text-[20px] text-ink" data-id="element-3141" data-mp="2iyc4z11pcch7">Scope</h2>
          <p className="mt-3 max-w-prose text-[16px] leading-relaxed text-body" data-id="element-3142" data-mp="24yax31n0s4s8">{project.description}</p>
        </section>

        <section aria-labelledby="progress-heading" data-id="element-3143" data-mp="28z4inoczp7hy">
          <h2 id="progress-heading" className="font-display text-[20px] text-ink" data-id="element-3144" data-mp="tjnuj5co94lt">
            Tasks
          </h2>
          {total === 0 ? <p className="mt-3 text-[14px] text-muted" data-id="element-3145" data-mp="xn3p7w56kuvv">No tasks yet.</p> : <>
              <p className="mt-2 text-[14px] text-body" data-id="element-3146" data-mp="307oah3cmm58t">
                {taskSummary.done} of {total} done
              </p>
              <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-charcoal" role="img" aria-label={order.map(s => `${taskSummary[s]} ${TASK_STATUS_LABELS[s].toLowerCase()}`).join(', ')} data-id="element-3147" data-mp="2qimrr5ep59r5">
                {order.map(s => taskSummary[s] > 0 && <span key={s} className={colour[s]} style={{
              width: `${taskSummary[s] / total * 100}%`
            }} data-id="element-3148" data-mp="2hizsap5j2e0q" />)}
              </div>
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-muted" data-id="element-3149" data-mp="2cky7nzsge6n5">
                {order.map(s => <li key={s} className="flex items-center gap-2" data-id="element-3150" data-mp="22kxjo0jn32cm">
                    <span className={`h-2 w-2 rounded-full ${colour[s]}`} aria-hidden="true" data-id="element-3151" data-mp="22ms6jkeh14jc" />
                    {TASK_STATUS_LABELS[s]} {taskSummary[s]}
                  </li>)}
              </ul>
            </>}
        </section>

        {can(role, 'projects.update') && <ProjectSettings orgId={orgId} detail={detail} data-id="element-3152" data-mp="9a2ycxin8plc" />}
      </div>

      <aside data-id="element-3153" data-mp="dav9jwb2t4k9">
        <h2 className="text-[15px] font-medium text-ink" data-id="element-3154" data-mp="dalqksog7xo2">People on this project</h2>
        <ul className="mt-4 space-y-4" data-id="element-3155" data-mp="i3od4gut66ur">
          {members.map(m => <li key={m.userId} className="flex items-center gap-3" data-id="element-3156" data-mp="n1ge00dfl3b3">
              <Avatar user={m.user} data-id="element-3157" data-mp="n3uckj7bkxne" />
              <span className="min-w-0" data-id="element-3158" data-mp="3mhd68epabhxm">
                <span className="block text-[14px] text-ink" data-id="element-3159" data-mp="3mio643fm05jg">{displayName(m.user)}</span>
                <span className="block text-[12px] text-muted" data-id="element-3160" data-mp="3qiynec8uqidm">
                  {m.user.isInternal ? 'Cavalla' : m.user.jobTitle ?? 'Invited'}, {MEMBER_ROLE_LABELS[m.role].toLowerCase()}
                </span>
              </span>
            </li>)}
        </ul>
      </aside>
    </div>;
}
function ProjectSettings({
  orgId,
  detail
}: {
  orgId: string;
  detail: ProjectDetail;
}) {
  const [status, setStatus] = React.useState<ProjectStatus>(detail.project.status);
  const [endDate, setEndDate] = React.useState(detail.project.endDate ?? '');
  const save = usePortalMutation(updateProject);
  const dirty = status !== detail.project.status || endDate !== (detail.project.endDate ?? '');
  return <section aria-labelledby="settings-heading" className="rounded-md border border-line p-5" data-id="element-3161" data-mp="2altwwei1yl15">
      <h2 id="settings-heading" className="text-[15px] font-medium text-ink" data-id="element-3162" data-mp="25rzi9phb0w8x">
        Project settings
      </h2>
      <p className="mt-1 text-[13px] text-muted" data-id="element-3163" data-mp="2kglxzsaufq0o">Visible to owners and admins.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-3164" data-mp="2fiu4itujvvud">
        <div data-id="element-3165" data-mp="2tiv7qfyaffd0">
          <label htmlFor="project-status" className="block text-[13px] font-medium text-ink" data-id="element-3166" data-mp="2tjdbdxna4vuy">
            Status
          </label>
          <select id="project-status" value={status} onChange={e => setStatus(e.target.value as ProjectStatus)} className={`${inputClass} mt-1.5`} data-id="element-3167" data-mp="2okjtzu4vgkpi">
            {(Object.keys(PROJECT_STATUS_LABELS) as ProjectStatus[]).map(s => <option key={s} value={s} data-id="element-3168" data-mp="35tcnewctujb0">
                {PROJECT_STATUS_LABELS[s]}
              </option>)}
          </select>
        </div>
        <div data-id="element-3169" data-mp="3ar4gvut4edhb">
          <label htmlFor="project-end" className="block text-[13px] font-medium text-ink" data-id="element-3170" data-mp="3arnyfaeuyzut">
            End date
          </label>
          <input id="project-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={`${inputClass} mt-1.5`} data-id="element-3171" data-mp="312mkdprvisjh" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3" data-id="element-3172" data-mp="2n1juf1xrcy3a">
        <Button size="sm" disabled={!dirty || save.pending} onClick={() => void save.run(orgId, detail.project.id, {
        status,
        endDate: endDate || null
      })} data-id="element-3173" data-mp="2s2hdrhui7y6d">
          {save.pending ? 'Saving' : 'Save changes'}
        </Button>
        <InlineError error={save.error} data-id="element-3174" data-mp="2i4jnkot33rb4" />
      </div>
    </section>;
}
function Deliverables({
  orgId,
  projectId,
  role
}: {
  orgId: string;
  projectId: string;
  role: OrgRole;
}) {
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listDeliverables(orgId, projectId), [orgId, projectId]);
  const [dialog, setDialog] = React.useState<{
    open: boolean;
    versionOf: DeliverableGroup | null;
  }>({
    open: false,
    versionOf: null
  });
  return <div data-id="element-3175" data-mp="2sfux7iweel92">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" data-id="element-3176" data-mp="2dovvmrtzcwyz">
        <p className="text-[14px] text-muted" data-id="element-3177" data-mp="2dmsvvg30zph2">Every download uses a fresh link that expires after five minutes, and is recorded.</p>
        {can(role, 'deliverables.upload') && <Button size="sm" onClick={() => setDialog({
        open: true,
        versionOf: null
      })} data-id="element-3178" data-mp="36e2r1hy516g0">
            <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-3179" data-mp="3bbbe9wy580o4" />
            Upload
          </Button>}
      </div>
      <QueryState loading={loading} error={error} data-id="element-3180" data-mp="2xclhkif8k30a">
        {data && data.length === 0 ? <EmptyState title="No deliverables yet" body="Files the team shares on this project will appear here." data-id="element-3181" data-mp="31cn75t7p55z7" /> : data && <DeliverableList orgId={orgId} role={role} groups={data} onNewVersion={group => setDialog({
        open: true,
        versionOf: group
      })} data-id="element-3182" data-mp="1pzb7luiyz9iu" />}
      </QueryState>
      <UploadDeliverableDialog open={dialog.open} onClose={() => setDialog({
      open: false,
      versionOf: null
    })} orgId={orgId} projectId={projectId} versionOf={dialog.versionOf} data-id="element-3183" data-mp="1idxpn61mdnn6" />
    </div>;
}
function ProjectMessages({
  orgId,
  projectId,
  projectName,
  role
}: {
  orgId: string;
  projectId: string;
  projectName: string;
  role: OrgRole;
}) {
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listConversations(orgId), [orgId]);
  const conversation = data?.find(c => c.projectId === projectId);
  const [draft, setDraft] = React.useState('');
  const start = usePortalMutation(createConversation);
  return <QueryState loading={loading} error={error} data-id="element-3184" data-mp="19h6wp2id6owt">
      {conversation ? <div className="max-w-3xl" data-id="element-3185" data-mp="14msvukkxnpkr">
          <MessageThread orgId={orgId} conversationId={conversation.id} canSend={can(role, 'messages.send')} data-id="element-3186" data-mp="14mj6mz7ppmg4" />
        </div> : <form className="max-w-3xl" onSubmit={e => {
      e.preventDefault();
      void start.run(orgId, {
        subject: projectName,
        projectId,
        body: draft
      });
    }} data-id="element-3187" data-mp="c0hvnb18nqoh">
          <p className="text-[15px] text-body" data-id="element-3188" data-mp="15o4iz8ma0gal">No messages on this project yet. Start the conversation with the team.</p>
          <label htmlFor="first-message" className="sr-only" data-id="element-3189" data-mp="1agxm5s23jgkk">
            Message
          </label>
          <textarea id="first-message" rows={4} value={draft} onChange={e => setDraft(e.target.value)} className={`${inputClass} mt-4 resize-y`} data-id="element-3190" data-mp="1fegyunj5blae" />
          <div className="mt-3 flex items-center gap-3" data-id="element-3191" data-mp="mq1owfevft5x">
            <Button type="submit" size="sm" disabled={!draft.trim() || start.pending} data-id="element-3192" data-mp="msnui9i7byli">
              Send
            </Button>
            <InlineError error={start.error} data-id="element-3193" data-mp="wohg0idwf2ff" />
          </div>
        </form>}
    </QueryState>;
}